import { createRoot } from 'react-dom/client';
import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

const typeConfig: Record<ToastType, { icon: typeof Info; ring: string; iconColor: string }> = {
  success: { icon: CheckCircle2, ring: 'border-green-500/40', iconColor: 'text-green-500' },
  error: { icon: XCircle, ring: 'border-red-500/40', iconColor: 'text-red-500' },
  info: { icon: Info, ring: 'border-blue-500/40', iconColor: 'text-blue-500' },
  warning: { icon: AlertTriangle, ring: 'border-amber-500/40', iconColor: 'text-amber-500' },
};

function ToastShell({ message, type, duration, onDone }: { message: string; type: ToastType; duration: number; onDone: () => void }) {
  const [visible, setVisible] = useState(false);
  const cfg = typeConfig[type];
  const Icon = cfg.icon;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const hide = setTimeout(() => setVisible(false), duration);
    const done = setTimeout(onDone, duration + 250);
    return () => { clearTimeout(hide); clearTimeout(done); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`fixed top-4 right-4 z-[10001] max-w-sm transition-all duration-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
      role="status"
      aria-live="polite"
    >
      <div className={`flex items-start gap-3 bg-white dark:bg-gray-800 border ${cfg.ring} rounded-lg shadow-xl px-4 py-3`}>
        <div className={`shrink-0 mt-0.5 ${cfg.iconColor}`}><Icon size={18} /></div>
        <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap leading-relaxed">{message}</p>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Schließen"
        >
          <XCircle size={16} />
        </button>
      </div>
    </div>
  );
}

/**
 * Imperativer, selbst-schließender Toast — Ersatz fuer das verbotene window.alert() (Regel #4).
 * Mountet sich via createRoot (analog utils/confirmDialog.tsx), kein Provider noetig.
 *
 * @example
 *   toast('Gespeichert');
 *   toast('Fehler beim Import: ' + e.message, 'error');
 */
export function toast(message: string, type?: ToastType, durationMs = 4000): void {
  // Ohne expliziten Typ: Fehler am Text erkennen (die Mehrheit der abgeloesten alert()s sind
  // Fehlermeldungen). So gibt das blinde alert(x) -> toast(x) automatisch korrektes Styling.
  const resolvedType: ToastType = type ?? (/fehler|error|fehlgeschlagen|ung(ü|ue)ltig|nicht m(ö|oe)glich|konnte nicht|failed/i.test(message) ? 'error' : 'info');
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  const done = () => {
    root.unmount();
    if (container.parentNode) container.parentNode.removeChild(container);
  };

  root.render(<ToastShell message={message} type={resolvedType} duration={durationMs} onDone={done} />);
}

export default toast;
